import { Component, Input, ViewChild, ElementRef, forwardRef, AfterViewInit, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, debounceTime, filter, switchMap, tap, catchError } from 'rxjs/operators';
import {
    FormControl,
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    Validator,
    AbstractControl,
    ValidationErrors,
    NG_VALIDATORS
} from '@angular/forms';
import { ISCDUser } from '@app-root-interfaces';
import { ScdService } from '@app-root-services';
import { TinyHelper } from '../../helper/tiny-helper';
import { TranslateService } from '@ngx-translate/core';
import { TypeaheadMatch } from '@siemens/element-ng/typeahead';

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ScdSearchComponent),
    multi: true
};
const CUSTOM_VALIDATOR = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => ScdSearchComponent),
    multi: true
};

@Component({
    selector: 'app-scd-search',
    templateUrl: './scd-search.component.html',
    styleUrls: ['./scd-search.component.scss'],
    standalone: false,
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, CUSTOM_VALIDATOR]
})

// Code form the PWO component.
export class ScdSearchComponent implements AfterViewInit, ControlValueAccessor, Validator {
    private service = inject(ScdService);
    private translateService = inject(TranslateService);

    @ViewChild('userInput', { static: true }) userInput: ElementRef<HTMLInputElement>;
    @Input() placeholder: string;
    @Input() hint = 'e.g. Email / Firstname Lastname / Lastname, Firstname / GID';
    @Input() forSP = false;
    @Input() multiple = true;
    @Input() required: boolean;
    @Input() filter: { attribute: string; value: any }[];

    private _value: any = '';
    get value(): any {
        return this._value;
    }
    set value(v: any) {
        if (v !== this._value) {
            this._value = v;

            if (this.multiple) {
                if (this.forSP) {
                    // output multiple users in SP format
                    this.onChange({
                        results: v
                    });
                } else {
                    // output mutiple users as array
                    this.onChange(v);
                }
            } else {
                // output single user
                this.onChange(v[0]);
            }
        }
    }

    userCtrl = new FormControl();
    selectedUsers: ISCDUser[] = [];
    scdSearchResult: BehaviorSubject<(ISCDUser & { displayName: string })[]>;

    isLoading = false;
    disabled = false;
    userNotFound = false;

    constructor() {
        this.translateService
            .get('profiles-list-scd-search-hint')
            .pipe(tap((value) => (this.hint = value)))
            .subscribe();
    }

    /* eslint-disable @typescript-eslint/no-empty-function */
    onChange = (_: any) => {};
    /* eslint-disable @typescript-eslint/no-empty-function */
    onTouched = () => {};

    /* Methods for ControlValueAccessor */
    writeValue(value: any): void {
        if (value) {
            if (this.multiple) {
                if (typeof value !== 'object' || value.length === undefined) {
                    throw new Error('Value must be an array of IScdUser objects!');
                }
                this.selectedUsers = value;
            } else {
                if (typeof value !== 'object') {
                    throw new Error('Value must be an IScdUser object!');
                }
                this.selectedUsers = [value];
            }
        } else {
            this.selectedUsers = [];
        }
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    validate(_control: AbstractControl): ValidationErrors {
        // Custom validator is necessary because of SharePoints { result: [] } format
        return this.isValid() ? null : { required: true };
    }

    ngAfterViewInit() {
        this.scdSearchResult = new BehaviorSubject([]);

        this.userCtrl.valueChanges
            .pipe(
                debounceTime(225),
                distinctUntilChanged(),
                filter((val) => val && val.length > 0),
                switchMap((term) => {
                    this.isLoading = true;
                    return this.service.searchUser(term).pipe(
                        catchError(() => {
                            this.isLoading = false;
                            return [];
                        })
                    );
                })
            )
            .subscribe((result: ISCDUser[]) => {
                if (result.length === 0) {
                    this.scdSearchResult.next([]);
                    this.isLoading = false;
                } else {
                    const filteredResults = this.filterScdSearch(result).map((user) => ({
                        ...user,
                        displayName: `${user.firstName} ${user.surName} | ${user.department}`
                    }));
                    this.scdSearchResult.next(filteredResults);
                    this.isLoading = false;
                }
            });
    }

    filterScdSearch(result: ISCDUser[]): ISCDUser[] {
        if (!this.filter) {
            return result;
        }

        return this.filter.reduce(
            (filteredResults, attributeFilter) =>
                filteredResults.filter(
                    (person) =>
                        !person[attributeFilter.attribute] ||
                        person[attributeFilter.attribute] === attributeFilter.value
                ),
            [...result]
        );
    }

    blur(): void {
        this.onTouched();
        this.isValid();
    }

    isValid(): boolean {
        const isValid = this.required === undefined || this.selectedUsers.length > 0;
        return isValid;
    }

    remove(user: ISCDUser): void {
        if (this.multiple) {
            const index = this.selectedUsers.indexOf(user);
            if (index >= 0) {
                this.selectedUsers.splice(index, 1);
                this.update();
            }
        } else {
            this.selectedUsers = [];
            this.update();
        }
        this.getFocus();
    }

    getFocus(): void {
        // wait until the input is visible again, otherwise focus won't work
        setTimeout(() => {
            this.userInput.nativeElement.focus();
        });
    }

    selected(event: { option: { value: ISCDUser } }): void {
        const user: ISCDUser = event.option.value;
        this.scdSearchResult.next([]);
        this.userCtrl.setValue('');
        this.updateSelected(user);
        this.userInput.nativeElement.value = '';
    }

    onTypeaheadSelect(match: TypeaheadMatch): void {
        const candidates = this.scdSearchResult.getValue();
        const matchedText = match?.text ?? (match?.option as { displayName?: string })?.displayName;
        const user = candidates.find((u) => u.displayName === matchedText) ?? (match.option as unknown as ISCDUser);
        this.scdSearchResult.next([]);
        this.userCtrl.setValue('');
        this.updateSelected(user);
        this.userInput.nativeElement.value = '';
    }

    protected getTitle(user: ISCDUser): string {
        return `${user.surName}, ${user.firstName} (${user.department})`;
    }

    updateSelected(value: ISCDUser): void {
        if (this.multiple) {
            this.selectedUsers.push(value);
        } else {
            this.selectedUsers = [value];
            this.userInput.nativeElement.blur();
        }
        this.update();
    }

    update(): void {
        // Deep copy, otherwise angular won't run the validator function
        this.value = TinyHelper.deepCopy(this.selectedUsers);
    }
}
